import { Account } from "models/account";
import { Button, Card, Icon, Label } from "semantic-ui-react";
import "./plan-select.scss";
import PlanForm from "models/forms/plan-form";
import { observer } from "mobx-react-lite";
import { Plan } from "../../../models/plan";
import { AppStore } from "../../../stores";
import { PromoCode, PromoCodeDuration } from "../../../models/promo-code";
import { CardElement } from "@stripe/react-stripe-js";
import { RefObject, useEffect, useRef } from "react";
import { toCurrency } from "../../../core/utils";
import { AccountPlan } from "../../../models/account-plan";

interface Props {
  form: PlanForm;
  account: Account;
  windowsOnly?: boolean;
  linuxOnly?: boolean;
  newPlanTierSelection?: number;
  setNewPlanTierSelection: (newPlanTierSelection: number) => any;
  promoCode?: PromoCode;
  setAppliedPromoCode?: (promoCode?: PromoCode) => any;
  planForm?: PlanForm;
  changePayment?: boolean;
  validatePromoCode?: () => any;
  promoCodeError?: boolean;
  promoCodeRef?: RefObject<HTMLInputElement>;
  promoCodeSubmitting?: boolean;
  forSetup: boolean;
}

interface CardProps {
  currentPlanId?: number;
  originalMonthlyPrice: number;
  discountedMonthlyPrice: number;
  includes: string[];
  plan: Plan;
  newPlanTierSelection?: number;
  setNewPlanTierSelection: (newPlanTierSelection: number) => any;
  promoCode?: PromoCode;
  forSetup: boolean;
  isFirst?: boolean;
  customTitle?: string;
  customButtonText?: string;
  subheaderText?: string;
}

const PlanCard = ({
                    currentPlanId,
                    originalMonthlyPrice,
                    discountedMonthlyPrice,
                    plan,
                    includes,
                    newPlanTierSelection,
                    setNewPlanTierSelection,
                    promoCode,
                    forSetup,
                    isFirst,
                    customTitle,
                    customButtonText,
                    subheaderText,
                  }: CardProps) => {

  let showAsCurrentPlan = currentPlanId == plan.id;
  // if (AppStore.account.setupComplete)
    showAsCurrentPlan = currentPlanId && currentPlanId == plan.id;

  let buttonBackgroundColor = "#01ab48";
  let buttonTextColor = "#FFF";
  let buttonIcon: JSX.Element;
  let buttonText = "Current Plan";
  if (showAsCurrentPlan && newPlanTierSelection && plan.tier != newPlanTierSelection) {
    buttonBackgroundColor = "#FFF";
    buttonTextColor = "#767676";
  } else if (showAsCurrentPlan) {
    buttonBackgroundColor = "#FFF";
    buttonTextColor = "#1ebc30";
    buttonIcon = <Icon name={"check"} />;
  } else if (plan.tier == newPlanTierSelection) {
    buttonBackgroundColor = "#FFF";
    buttonTextColor = "#01ab48";
    buttonIcon = <Icon name={"check"} />;
    buttonText = `${customTitle || plan.name} Plan Selected`;
  } else {
    buttonText = `Select ${customTitle || plan.name} Plan`;
  }

  function getDurationText(): string {
    if (!forSetup)
      return "";

    if (!promoCode)
      return "";

    switch (promoCode.duration) {
      case PromoCodeDuration.forever:
        return "";
      case PromoCodeDuration.once:
        return "for first month";
      case PromoCodeDuration.repeating:
        return `for first ${promoCode.durationInMonths} months, then ${toCurrency(originalMonthlyPrice)}/mo`;
    }
  }

  return (<Card>
      <Card.Content>
        <Card.Header>{customTitle || plan.name}</Card.Header>
        {isFirst && forSetup && (
          <p style={{
            fontSize: "14px",
            fontWeight: 500,
            textAlign: "left",
            fontFamily: "'Inter', sans-serif",
            color: "#57647c",
            lineHeight: 1.4,
            margin: "10px 10px"
          }}>
            Get started for <strong>FREE</strong><br/>then pay as you grow
          </p>
        )}
        {subheaderText && forSetup && (
          <p style={{
            fontSize: "14px",
            fontWeight: 500,
            textAlign: "left",
            fontFamily: "'Inter', sans-serif",
            color: "#57647c",
            lineHeight: 1.4,
            margin: "10px 10px"
          }}>
            {subheaderText}
          </p>
        )}
        {promoCode && (
          <div
            className={`original-price-container${!promoCode.applicablePlanTiers.includes(plan.tier) ? " hidden" : ""}`}>
            <div className={"original-price"}>{originalMonthlyPrice % 100 === 0 ? `$${(originalMonthlyPrice / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : toCurrency(originalMonthlyPrice)}</div>
          </div>
        )}
        {isFirst && forSetup ? (
          <div className="price">
            <h3>$0</h3>
            <span>for 10 hr/mo then $1.20/hr</span>
          </div>
        ) : (
          <div id={`tier-${plan.tier}-plan-discounted-price`} className="price">
            <h3>{discountedMonthlyPrice % 100 === 0 ? `$${(discountedMonthlyPrice / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : toCurrency(discountedMonthlyPrice)}</h3>
            <span>/mo</span>
          </div>
        )}
        <div className={"promo-code-duration"}>{getDurationText()}</div>
        {promoCode && (
          promoCode.applicablePlanTiers.includes(plan.tier) ? (
            <div className={"promo-code-applied"}>Promo <strong>{promoCode.customerFacingCode}</strong> Applied!</div>
          ) : (
            <div className={"promo-code-not-applicable"}>Promo <strong>{promoCode.customerFacingCode}</strong> not
              applicable to this plan</div>
          )
        )}
      </Card.Content>
      <Card.Content extra>
        <Button
          style={{ background: buttonBackgroundColor, color: buttonTextColor }}
          onClick={() => {
            setNewPlanTierSelection(plan.tier == newPlanTierSelection ? null : plan.tier);
          }}
        >
          {buttonIcon}
          {customButtonText || buttonText}
        </Button>
      </Card.Content>
      <Card.Content>
        <div style={{ margin: "0px 10px" }}>
          {includes.map((item, index) => (
            <div key={index} className="include">
              <Icon name={"check"} /> <span dangerouslySetInnerHTML={{ __html: item }} />
            </div>
          ))}
        </div>
      </Card.Content>
    </Card>
  );
};

const planIncludes = {
  1: ["10 test execution hours", "10 parallel test runners", "Reporting & Analytics", "Test Artifacts", "CI/CD Integration", "Command-line Executable", "API for your Tests"],
  2: ["<strong>10</strong> Test Execution Hours", "<strong>10</strong> Parallel Test Runners", "Reporting & Analytics", "Test Artifacts", "CI/CD Integration", "Command-Line Executable", "API for your Tests"],
  3: ["<strong>500</strong> Test Execution Hours", "<strong>30</strong> Parallel Test Runners", "Reporting & Analytics", "Test Artifacts", "CI/CD Integration", "Command-Line Executable", "API for your Tests"],
  4: ["<strong>1000</strong> Test Execution Hours", "<strong>30</strong> Parallel Test Runners", "Reporting & Analytics", "Test Artifacts", "CI/CD Integration", "Command-Line Executable", "API for your Tests"],
  9: ["10 test execution hours", "10 parallel test runners", "Reporting & Analytics", "Test Artifacts", "CI/CD Integration", "Command-line Executable", "API for your Tests", "Claude integration"],
};

const LinuxPane = observer(({ form, newPlanTierSelection, setNewPlanTierSelection, promoCode, forSetup }: Props) => {
  const enterpriseIncludes = [
    "High Volume Parallel Tests",
    "Custom Test Runners",
    "Advanced Workflows"
  ];

  return (
    <div>
      <Card.Group itemsPerRow={4}>
        {AppStore.availablePlans.map((plan, index) => {
          const customTitles = ["Growth", "Team", "Scaling"];
          const subheaderTexts = [undefined, "For teams running small to medium sized test suites", "For companies running larger test suites and testing multiple projects"];
          return <PlanCard key={plan.id}
                           originalMonthlyPrice={plan.monthlyPrice}
                           discountedMonthlyPrice={getPriceAfterDiscount(plan, promoCode)}
                           newPlanTierSelection={newPlanTierSelection}
                           setNewPlanTierSelection={setNewPlanTierSelection}
                           includes={planIncludes[plan.tier]}
                           plan={plan}
                           currentPlanId={AppStore.account.currentAccountPlan?.planId}
                           promoCode={promoCode}
                           forSetup={forSetup}
                           isFirst={index === 0}
                           customTitle={customTitles[index]}
                           subheaderText={subheaderTexts[index]} />;
        })}
        {forSetup && (
          <Card>
            <Card.Content>
              <Card.Header>Enterprise</Card.Header>
              <p style={{
                fontSize: "14px",
                fontWeight: 500,
                textAlign: "left",
                fontFamily: "'Inter', sans-serif",
                color: "#57647c",
                lineHeight: 1.4,
                margin: "10px 10px"
              }}>
                For enterprises with large-scale test runs and more complex testing needs.
              </p>
              <p style={{
                fontSize: "14px",
                fontWeight: 500,
                textAlign: "left",
                fontFamily: "'Inter', sans-serif",
                color: "#57647c",
                lineHeight: 1.4,
                margin: "10px 10px"
              }}>
                Run hundreds of parallel tests that span multiple code repositories.
              </p>
            </Card.Content>
            <Card.Content extra>
              <Button
                onClick={() => window.open("https://testery.io/talk-to-sales", "_blank")}
              >
                Talk to sales
              </Button>
            </Card.Content>
            <Card.Content>
              <div style={{ margin: "0px 10px" }}>
                {enterpriseIncludes.map((item, index) => (
                  <div key={index} className="include">
                    <Icon name={"check"} /> {item}
                  </div>
                ))}
              </div>
            </Card.Content>
          </Card>
        )}
      </Card.Group>
      {form.hasErrors("linuxPlanTier") && (
        <Label className="plan-error" color="red">
          {form.getErrorText("linuxPlanTier")}
        </Label>
      )}
    </div>
  );
});

export function getPriceAfterDiscount(plan: Plan, promoCode: PromoCode): number {
  if (!promoCode || !promoCode.applicablePlanTiers.includes(plan.tier))
    return plan.monthlyPrice;

  if (promoCode.percentOff) {
    return plan.monthlyPrice * ((100 - promoCode.percentOff) / 100);
  } else {
    return plan.monthlyPrice - promoCode.amountOff;
  }
}

export function getCurrentPlanAndPricingText(accountPlan: AccountPlan, plan: Plan) {
  let text = "";
  if (!accountPlan.promoCode) {
    text = `You are currently on the ${plan.name} plan and paying ${toCurrency(accountPlan.monthlyPrice)}/month.`;
  } else if (accountPlan.promoCodeDuration == PromoCodeDuration.forever) {
    text = `You are currently on the ${plan.name} plan using Promo Code ${accountPlan.promoCode} and paying ${toCurrency(accountPlan.monthlyPriceWithPromoCode)}/month.`;
  } else {
    if (accountPlan.promoCodeDuration == PromoCodeDuration.once)
      text = `You are currently on the ${plan.name} plan using Promo Code ${accountPlan.promoCode} and paying ${toCurrency(accountPlan.monthlyPriceWithPromoCode)}/month for the first month, then ${toCurrency(accountPlan.monthlyPrice)}/month after that.`;
    else
      text = `You are currently on the ${plan.name} plan using Promo Code ${accountPlan.promoCode} and paying ${toCurrency(accountPlan.monthlyPriceWithPromoCode)}/month for the first ${accountPlan.promoCodeDurationInMonths} months, then ${toCurrency(accountPlan.monthlyPrice)}/month after that.`;
  }
  return text;
}

const PlanSelect = observer(({
                               form,
                               account,
                               newPlanTierSelection,
                               setNewPlanTierSelection,
                               promoCode,
                               setAppliedPromoCode,
                               planForm,
                               changePayment,
                               validatePromoCode,
                               promoCodeSubmitting,
                               promoCodeError,
                               promoCodeRef,
                               forSetup,
                             }: Props) => {
  const paymentSectionRef = useRef<HTMLDivElement>(null);
  const planCardsRef = useRef<HTMLDivElement>(null);
  const previousSelectionRef = useRef<number>(null);

  const smoothScrollTo = (targetPosition: number, duration: number) => {
    const startPosition = window.pageYOffset;
    const distance = targetPosition - startPosition;
    let startTime: number | null = null;

    const animation = (currentTime: number) => {
      if (startTime === null) startTime = currentTime;
      const timeElapsed = currentTime - startTime;
      const progress = Math.min(timeElapsed / duration, 1);

      // Easing function for smooth animation
      const ease = (t: number) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

      window.scrollTo(0, startPosition + distance * ease(progress));

      if (timeElapsed < duration) {
        requestAnimationFrame(animation);
      }
    };

    requestAnimationFrame(animation);
  };

  useEffect(() => {
    if (forSetup) {
      if (newPlanTierSelection && paymentSectionRef.current) {
        // Plan selected - scroll down to payment section
        setTimeout(() => {
          const element = paymentSectionRef.current;
          if (element) {
            const yOffset = -20;
            const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
            window.scrollTo({ top: y, behavior: 'smooth' });
          }
        }, 150);
      } else if (!newPlanTierSelection && previousSelectionRef.current !== null) {
        // Plan unselected - scroll back to top of page with slower animation
        setTimeout(() => {
          smoothScrollTo(0, 800); // 800ms duration for slower scroll
        }, 100);
      }
      previousSelectionRef.current = newPlanTierSelection;
    }
  }, [newPlanTierSelection, forSetup]);

  const currentPlan = AppStore.account.currentPlan;
  const currentAccountPlan = AppStore.account.currentAccountPlan;
  const accountSetupComplete = AppStore.account.setupComplete;
  const linuxPane = <LinuxPane form={form} account={account} newPlanTierSelection={newPlanTierSelection}
                               setNewPlanTierSelection={setNewPlanTierSelection} promoCode={promoCode}
                               forSetup={forSetup} />;
  const content = accountSetupComplete && currentPlan && (currentPlan.name.toLowerCase().startsWith("contract") || AppStore.account.windowsRunners > 0) ? (
    <div style={{ marginBottom: "5px" }}>
      You are currently on a custom plan. Please <a style={{ textDecoration: "underline", color: "#0048ff" }}
                                                    href={"https://testery.atlassian.net/servicedesk/customer/portal/1"}
                                                    target={"_blank"}>create a ticket</a> if you'd like to make plan
      changes.
    </div>
  ) : (
    accountSetupComplete ? (
      <>
        <div style={{ marginBottom: "5px" }}>
          {getCurrentPlanAndPricingText(currentAccountPlan, currentPlan)}
        </div>
        {linuxPane}
      </>
    ) : (
      linuxPane
    )
  );

  return (
    <div className="plan-select">
      <Card.Group>
        <Card className="total">
          <Card.Content>
            <div ref={planCardsRef}>
              {content}
            </div>
          </Card.Content>
          {forSetup && newPlanTierSelection && (
            <div ref={paymentSectionRef}>
              <Card.Content>
                <h1 className="pricing-title" style={{ marginTop: "40px", marginBottom: "10px" }}>Payment Information</h1>
              </Card.Content>
              <Card.Content>
                <div className={"field promo-code-container"}>
                <input id={"signup-promo-code"} ref={promoCodeRef} placeholder="Promo code" onKeyDown={async (e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    await validatePromoCode();
                  }
                }} disabled={promoCodeSubmitting || promoCode != null} className={"promo-code-input"} />
                {promoCode ? (
                  <Button style={{ background: "transparent", color: "#bc0000" }}
                          onClick={() => setAppliedPromoCode(null)}>
                    <Icon name={"x"} /> Remove promo code
                  </Button>
                ) : (
                  <Button positive={true} disabled={promoCodeSubmitting} loading={promoCodeSubmitting}
                          onClick={validatePromoCode}><Icon name={"check"} /> Apply</Button>
                )}
              </div>
              <div className={`promo-code-error${promoCodeError && !promoCodeSubmitting ? " show" : ""}`}>
                Promo Code <strong>{promoCodeRef.current?.value?.toUpperCase()}</strong> is not currently valid
              </div>
            </Card.Content>
            {newPlanTierSelection && (promoCode == null || (promoCode && promoCode.requiresCreditCard) || !promoCode.applicablePlanTiers.includes(planForm.linuxPlanTier)) && (!account.setupComplete || (account.currentPlan.monthlyStripeSubscriptionId && (!account.cardLastFour || changePayment))) && (
              <Card.Content>
              <div className="field" style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <label style={{ display: "none" }}>Payment Information</label>
                <CardElement
                  id={"stripe-payment-info"}
                  options={{
                    style: {
                      base: {
                        fontSize: "16px",
                        color: "#424770",
                        "::placeholder": {
                          color: "#aab7c4",
                        },
                      },
                      invalid: {
                        color: "#9e2146",
                      },
                    },
                  }}
                />
                {planForm.hasErrors("cardToken") && (
                  <Label pointing prompt>
                    {planForm.getErrorText("cardToken")}
                  </Label>
                )}
              </div>
              </Card.Content>
            )}
            </div>
          )}
        </Card>
      </Card.Group>
    </div>
  );
});

export default PlanSelect;
